import React, { useState, useEffect } from 'react';
import { Search, Shield, MapPin, BarChart, Eye, EyeOff, Bug, RefreshCw } from 'lucide-react';
import axios from 'axios';

interface PrivacySetting {
    id: number;
    user_id: number;
    user_username: string;
    user_name?: string;
    user_avatar?: string;
    location_sharing_enabled: boolean;
    location_history_days: number;
    message_history_days: number;
    last_seen_visibility: boolean;
    online_status_visibility: boolean;
    analytics_enabled: boolean;
    crash_reporting_enabled: boolean;
    updated_at: string;
}

interface PrivacyStats {
    total_users: number;
    location_sharing_disabled: number;
    analytics_disabled: number;
}

const PrivacySettingsManagement: React.FC = () => {
    const [settings, setSettings] = useState<PrivacySetting[]>([]);
    const [stats, setStats] = useState<PrivacyStats | null>(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [searchText, setSearchText] = useState('');

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        setRefreshing(true);
        try {
            const [settingsRes, statsRes] = await Promise.all([
                axios.get('https://api.lifeshare.app/api/v1/admin/privacy-settings'),
                axios.get('https://api.lifeshare.app/api/v1/admin/privacy-settings/stats')
            ]);
            setSettings(settingsRes.data.settings || []);
            setStats(statsRes.data);
        } catch (error) {
            console.error('Fetch error:', error);
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    const BoolBadge = ({ value, trueText, falseText }: { value: boolean; trueText: string; falseText: string }) => (
        <span className={`px-2 py-0.5 rounded-xl text-xs font-medium ${value ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
            {value ? trueText : falseText}
        </span>
    );

    const filteredSettings = settings.filter(s =>
        s.user_username.toLowerCase().includes(searchText.toLowerCase()) ||
        (s.user_name && s.user_name.toLowerCase().includes(searchText.toLowerCase()))
    );

    if (loading) {
        return (
            <div className="flex items-center justify-center py-20">
                <div className="w-5 h-5 border-2 border-purple-300 border-t-purple-600 rounded-full animate-spin" />
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="max-w-5xl mx-auto space-y-6">

                {/* Header */}
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl md:text-3xl font-semibold text-gray-900">Gizlilik Ayarları</h1>
                        <p className="text-sm text-gray-500 mt-1">Kullanıcı gizlilik tercihlerini görüntüle</p>
                    </div>
                    <button onClick={fetchData} disabled={refreshing} className="p-2.5 bg-purple-600 text-white rounded-xl hover:bg-purple-700 transition-colors">
                        <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
                    </button>
                </div>

                {/* Stats */}
                {stats && (
                    <div className="grid grid-cols-3 gap-3">
                        <div className="bg-gradient-to-br from-purple-50 to-purple-100 border border-purple-200 rounded-2xl p-4">
                            <div className="flex items-center gap-2 mb-1 opacity-70"><Shield className="w-4 h-4" /></div>
                            <p className="text-2xl font-bold text-purple-700">{stats.total_users}</p>
                            <p className="text-xs text-purple-600">Toplam</p>
                        </div>
                        <div className="bg-gradient-to-br from-red-50 to-red-100 border border-red-200 rounded-2xl p-4">
                            <div className="flex items-center gap-2 mb-1 opacity-70"><MapPin className="w-4 h-4" /></div>
                            <p className="text-2xl font-bold text-red-700">{stats.location_sharing_disabled}</p>
                            <p className="text-xs text-red-600">Konum Kapalı</p>
                        </div>
                        <div className="bg-gradient-to-br from-orange-50 to-orange-100 border border-orange-200 rounded-2xl p-4">
                            <div className="flex items-center gap-2 mb-1 opacity-70"><BarChart className="w-4 h-4" /></div>
                            <p className="text-2xl font-bold text-orange-700">{stats.analytics_disabled}</p>
                            <p className="text-xs text-orange-600">Analytics Kapalı</p>
                        </div>
                    </div>
                )}

                {/* Search */}
                <div className="bg-white rounded-2xl p-4">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input type="text" placeholder="Kullanıcı ara..." value={searchText} onChange={(e) => setSearchText(e.target.value)} className="w-full pl-10 pr-4 py-3 bg-gray-50 rounded-xl text-sm placeholder-gray-400 outline-none focus:ring-2 focus:ring-purple-500" />
                    </div>
                </div>

                {/* List */}
                {filteredSettings.length === 0 ? (
                    <div className="bg-white rounded-2xl p-12 text-center">
                        <Shield className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                        <p className="text-gray-500">Gizlilik ayarı bulunamadı</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {filteredSettings.map((s) => (
                            <div key={s.id} className="bg-white rounded-2xl p-4 hover:ring-2 hover:ring-purple-100 transition-all">
                                <div className="flex items-start gap-3 mb-3">
                                    {s.user_avatar ? (
                                        <img src={`https://api.lifeshare.app${s.user_avatar}`} alt="" className="w-10 h-10 rounded-xl object-cover flex-shrink-0" />
                                    ) : (
                                        <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center text-purple-600 flex-shrink-0">
                                            <Shield className="w-5 h-5" />
                                        </div>
                                    )}
                                    <div className="min-w-0 flex-1">
                                        <p className="font-semibold text-gray-900 text-sm truncate">{s.user_name || s.user_username}</p>
                                        <p className="text-xs text-gray-500">@{s.user_username}</p>
                                    </div>
                                </div>

                                <div className="space-y-2 text-xs">
                                    <div className="flex items-center justify-between p-2 bg-gray-50 rounded-xl">
                                        <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />Konum Paylaşımı</span>
                                        <BoolBadge value={s.location_sharing_enabled} trueText="Açık" falseText="Kapalı" />
                                    </div>
                                    <div className="flex items-center justify-between p-2 bg-gray-50 rounded-xl">
                                        <span className="flex items-center gap-1">{s.last_seen_visibility ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}Son Görülme</span>
                                        <BoolBadge value={s.last_seen_visibility} trueText="Görünür" falseText="Gizli" />
                                    </div>
                                    <div className="flex items-center justify-between p-2 bg-gray-50 rounded-xl">
                                        <span className="flex items-center gap-1"><BarChart className="w-3 h-3" />Analytics</span>
                                        <BoolBadge value={s.analytics_enabled} trueText="Aktif" falseText="Kapalı" />
                                    </div>
                                    <div className="flex items-center justify-between p-2 bg-gray-50 rounded-xl">
                                        <span className="flex items-center gap-1"><Bug className="w-3 h-3" />Crash Raporlama</span>
                                        <BoolBadge value={s.crash_reporting_enabled} trueText="Aktif" falseText="Kapalı" />
                                    </div>
                                </div>

                                <div className="mt-2 pt-2 border-t border-gray-100 text-xs text-gray-400 text-right">
                                    Güncelleme: {new Date(s.updated_at).toLocaleDateString('tr-TR')}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default PrivacySettingsManagement;
